import { useState } from "react";
import { DocumentData, Project } from "types";

import { DocType, PDFStatus, UploadType } from "../types";
import {
  requiredDocsMapping,
  requiredExitOnlyDocs,
} from "../data/requiredDocsMapping";
import { usableDocs } from "../data/docDefinitions";
import { getLatestVersionDocs } from "pages/Documents/Documents";

export type DocumentDefinition = {
  label: string;
  type: DocType;
  revision: string;
  status: PDFStatus;
  visibility: string;
  location: string | undefined;
} & DocumentData;

export const useEnhancedDocuments = (
  project?: Project
): DocumentDefinition[] => {
  const [requiredDocs, setRequiredDocs] = useState<DocType[]>(
    (project && requiredDocsMapping[project.projectdata.category]) || []
  );

  if (!project) {
    return [];
  }

  const exitOnlyDocs = project.directives
    .filter((d) => d.is_exit)
    .map((d) => `${requiredExitOnlyDocs[d.name]}_${d.name}` as DocType);

  const docsIncludingNulls = [...requiredDocs, ...exitOnlyDocs].map((doc) => {
    const existingDoc = project.documents?.find(({ type }) => type === doc);

    if (!usableDocs[doc]) {
      return undefined;
    }

    const { completeFn, ...docSettings } = usableDocs[doc];

    const canRender = completeFn(project);
    const status = existingDoc
      ? PDFStatus.ready
      : canRender
      ? PDFStatus.pending
      : PDFStatus.missingData;

    return {
      ...docSettings,
      status,
      location: existingDoc?.location,
      revision: existingDoc?.version || "-",
    };
  });

  return docsIncludingNulls.filter((d): d is DocumentDefinition => Boolean(d));
};

export const useEnhancedDocumentsWithVersion = (
  project?: Project,
  documents?: DocumentData[]
): DocumentDefinition[] => {
  const [requiredDocs, setRequiredDocs] = useState<DocType[]>(
    (project && requiredDocsMapping[project.projectdata.category]) || []
  );

  if (!project) {
    return [];
  }

  const exitOnlyDocs = project.directives
    .filter((d) => d.is_exit)
    .map((d) => `${requiredExitOnlyDocs[d.name]}_${d.name}` as DocType);

  const documentsToCheck =
    (documents?.length && documents) || getLatestVersionDocs(project);

  const docsIncludingNulls = [...requiredDocs, ...exitOnlyDocs].map((doc) => {
    const existingDoc = documentsToCheck?.find(({ type }) => type === doc);

    if (!usableDocs[doc]) {
      return undefined;
    }

    const { completeFn, ...docSettings } = usableDocs[doc];

    const canRender = completeFn(project);
    const status = existingDoc
      ? PDFStatus.ready
      : canRender
      ? PDFStatus.pending
      : PDFStatus.missingData;

    return {
      ...docSettings,
      status,
      location: existingDoc?.key || existingDoc?.location,
      revision: existingDoc?.version || "-",
    };
  });

  return docsIncludingNulls.filter((d): d is DocumentDefinition => Boolean(d));
};

export const getEnhanceDocumentsWithVersion = (
  project: Project,
  documents?: DocumentData[]
): DocumentDefinition[] => {
  const requiredDocs =
    (project && requiredDocsMapping[project.projectdata.category]) || [];
  const exitOnlyDocs = project.directives
    .filter((d) => d.is_exit)
    .map((d) => `${requiredExitOnlyDocs[d.name]}_${d.name}` as DocType);

  const documentsToCheck =
    (documents?.length && documents) || getLatestVersionDocs(project);

  const docsIncludingNulls = [...requiredDocs, ...exitOnlyDocs].flatMap(
    (doc) => {
      const existingDocs = documentsToCheck?.filter(({ type }) => type === doc);

      if (!usableDocs[doc]) {
        return undefined;
      }

      return existingDocs.map((existingDoc) => {
        const { completeFn, ...docSettings } = usableDocs[doc];

        const canRender = completeFn(project);
        const status = existingDoc
          ? PDFStatus.ready
          : canRender
          ? PDFStatus.pending
          : PDFStatus.missingData;

        return {
          ...docSettings,
          ...existingDoc,
          status,
          location: existingDoc?.key || existingDoc?.location,
          revision: existingDoc?.version || "-",
        };
      });
    }
  );

  return docsIncludingNulls.filter((d): d is DocumentDefinition => Boolean(d));
};

function isRenderableDocument(value: UploadType): value is DocType {
  return Object.values(DocType).includes(value as DocType);
}

export const getEnhanceDocumentsForRevision = (
  project: Project,
  documents?: DocumentData[]
): DocumentDefinition[] => {
  const documentsToCheck =
    (documents?.length && documents) || getLatestVersionDocs(project);

  const documentTypesToCheckDedupe = documentsToCheck
    .filter((doc) => isRenderableDocument(doc.type))
    .map((doc) => doc.type as DocType);

  const documentTypesToCheck = documentTypesToCheckDedupe.filter(
    (item, index) => documentTypesToCheckDedupe.indexOf(item) === index
  );

  const docsIncludingNulls = documentTypesToCheck.flatMap((doc) => {
    const existingDocs = documentsToCheck?.filter(({ type }) => type === doc);

    if (!usableDocs[doc]) {
      return undefined;
    }

    return existingDocs.map((existingDoc) => {
      const { completeFn, ...docSettings } = usableDocs[doc];

      const canRender = completeFn(project);
      const status = existingDoc
        ? PDFStatus.ready
        : canRender
        ? PDFStatus.pending
        : PDFStatus.missingData;

      return {
        ...docSettings,
        ...existingDoc,
        status,
        location: existingDoc?.key || existingDoc?.location,
        revision: existingDoc?.version || "-",
      };
    });
  });

  return docsIncludingNulls.filter((d): d is DocumentDefinition => Boolean(d));
};
